import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

import { Shield, Mail, ArrowRight } from "lucide-react";
import { PasswordInput } from "@/components/ui/password-input";
import { SubmitButton } from "@/components/ui/submit-button";
import { FieldError } from "@/components/ui/field-error";
import { friendlyAuthError } from "@/lib/friendly-errors";
import { markSessionEphemeral } from "@/lib/remember-me";

export const Route = createFileRoute("/admin-login")({
  ssr: false,
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; oauth?: string; error?: string } => {
    const out: { redirect?: string; oauth?: string; error?: string } = {};
    if (typeof search.redirect === "string") out.redirect = search.redirect;
    if (typeof search.oauth === "string") out.oauth = search.oauth;
    if (typeof search.error === "string") out.error = search.error;
    return out;
  },
  head: () => ({
    meta: [
      { title: "Admin Login — Recovery Trauma™" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminLoginPage,
});



function AdminLoginPage() {
  const navigate = useNavigate();
  const { redirect: redirectTo } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const [resetLoading, setResetLoading] = useState(false);
  const [remember, setRemember] = useState(true);

  const handleForgotPassword = async () => {
    setError("");
    setInfo("");
    const target = email.trim();
    if (!target) {
      setError("Enter your admin email above, then click Forgot password.");
      return;
    }
    setResetLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(target, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetLoading(false);
    if (resetError) {
      setError(friendlyAuthError(resetError));
      return;
    }
    setInfo(`Password reset email sent to ${target}. Check your inbox (and spam).`);
  };

  const safeRedirect = (target?: string) => {
    if (target && target.startsWith("/admin")) return target;
    return "/admin";
  };


  // If already signed in, bounce to intended admin URL. The destination
  // route (`requireAdminOrDeny` on /admin, `requireAdmin` elsewhere) will
  // self-heal the role via `ensure_admin_role` and render a visible deny
  // state if the account isn't admin. Doing the check here too caused
  // two concurrent RPCs to contend for Supabase's auth Web Lock on mobile
  // Safari — one would hang and sign the user out with "no admin access".
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled || !data.user) return;
      window.location.assign(safeRedirect(redirectTo));
    });
    return () => {
      cancelled = true;
    };
  }, [redirectTo]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setInfo("");
    setLoading(true);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (signInError || !data.user) {
      setLoading(false);
      setError(friendlyAuthError(signInError));
      return;
    }
    markSessionEphemeral(remember);
    // Let /admin's requireAdminOrDeny do the role check + self-heal.
    window.location.assign(safeRedirect(redirectTo));
  };


  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
            <Shield className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-serif text-2xl text-foreground">Admin sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Restricted access. Admin credentials only.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Email</label>
            <div className="relative">
              <Mail className="absolute start-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="w-full rounded-lg border border-input bg-background py-2 ps-9 pe-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-1 block text-sm font-medium text-foreground">Password</label>
            <PasswordInput
              id="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              enterKeyHint="go"
              aria-describedby={error ? "admin-login-error" : undefined}
              className="w-full min-h-[44px] rounded-lg border border-input bg-background py-2 ps-9 pe-12 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground/80">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-input text-[var(--gold-deep)] focus:ring-ring"
            />
            Remember me on this device
          </label>

          <FieldError id="admin-login-error" message={error} />

          {info && (
            <p role="status" className="rounded-lg bg-emerald-50 p-2.5 text-xs text-emerald-700">
              {info}
            </p>
          )}

          <SubmitButton
            pending={loading}
            pendingLabel="Signing in…"
            disabled={!email || !password}
            trailingIcon={<ArrowRight className="h-4 w-4" />}
            className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-gold py-2.5 text-sm font-medium text-white transition-colors hover:bg-gold/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Sign in to admin
          </SubmitButton>

          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={resetLoading}
            className="block w-full text-center text-xs text-[var(--gold-deep)] hover:underline disabled:opacity-50"
          >
            {resetLoading ? "Sending reset email..." : "Forgot password?"}
          </button>

          <button
            type="button"
            onClick={() => navigate({ to: "/login" })}
            className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
          >
            Not an admin? Go to regular sign in
          </button>
        </form>
      </div>
    </div>
  );
}
